/**
 * FortuneWheelIntegration.js
 * 
 * This file provides integration functions to connect your existing Fortune Wheel
 * game with the off-chain database approach for lottery tickets.
 */

import * as LotteryDatabase from './GameDrawWheelDatabase';

/**
 * Generates wheel segments based on participant data from the database
 * for use with the ReusableFortuneWheel component in QuickCashGame
 * @param {string} batchId - The batch ID
 * @param {string} userId - The current user's ID
 * @returns {Array} - Array of wheel segments formatted for ReusableFortuneWheel
 */
export const generateWheelSegmentsForQuickCash = (batchId, userId) => {
  if (!batchId) {
    // Return default segments if no batch ID is provided
    return [
      { name: "You", color: "rgba(66, 153, 225, 0.9)", value: 25000, probability: 12, isPlayer: true },
      { name: "Player 1", color: "rgba(56, 178, 172, 0.9)", value: 0, probability: 25 },
      { name: "Player 2", color: "rgba(49, 130, 206, 0.9)", value: 0, probability: 20 },
      { name: "Player 3", color: "rgba(44, 82, 130, 0.9)", value: 0, probability: 18 },
      { name: "Player 4", color: "rgba(26, 54, 93, 0.9)", value: 0, probability: 15 },
      { name: "Player 5", color: "rgba(0, 30, 60, 0.9)", value: 0, probability: 10 }
    ];
  }
  
  // Get batch lottery data
  const batchLottery = LotteryDatabase.getBatchLottery(batchId);
  
  // If draw is already complete, show the result with winner highlighted
  if (batchLottery.isDrawComplete && batchLottery.drawResult) {
    const isWinner = batchLottery.drawResult.winner === userId;
    
    // Return a single segment for the winner
    return [
      { 
        name: isWinner ? "You" : "Winner", 
        color: isWinner ? "rgba(66, 153, 225, 0.9)" : "rgba(56, 178, 172, 0.9)", 
        value: batchLottery.drawResult.prizeAmount, 
        probability: 100,
        isPlayer: isWinner
      }
    ];
  }
  
  // Get all participants' tickets
  const participants = LotteryDatabase.getAllParticipantTickets(batchId);
  
  // Calculate total tickets
  const totalTickets = participants.reduce((sum, p) => sum + p.ticketCount, 0);
  
  // Format segments for the wheel
  const wheelSegments = [];
  
  // Add current user segment first (if they have tickets)
  const userParticipant = participants.find(p => p.userId === userId);
  if (userParticipant && userParticipant.ticketCount > 0) {
    const userProbability = (userParticipant.ticketCount / totalTickets) * 100;
    wheelSegments.push({
      name: "You", 
      color: "rgba(66, 153, 225, 0.9)", 
      value: 25000, 
      probability: userProbability,
      isPlayer: true
    });
  }
  
  // Add other participants (up to 5 more)
  const otherParticipants = participants
    .filter(p => p.userId !== userId)
    .sort((a, b) => b.ticketCount - a.ticketCount)
    .slice(0, 5);
  
  // Colors for other participants
  const colors = [
    "rgba(56, 178, 172, 0.9)", // Teal
    "rgba(49, 130, 206, 0.9)", // Blue
    "rgba(44, 82, 130, 0.9)",  // Dark blue
    "rgba(26, 54, 93, 0.9)",   // Navy
    "rgba(0, 30, 60, 0.9)"      // Deep blue
  ];
  
  // Add other participants
  otherParticipants.forEach((participant, index) => {
    const probability = (participant.ticketCount / totalTickets) * 100;
    wheelSegments.push({
      name: `Player ${index + 1}`,
      color: colors[index % colors.length],
      value: 0, // Only the winner gets the value
      probability: probability
    });
  });
  
  // If no participants or only the user, add placeholder segments
  if (wheelSegments.length === 0) {
    return [
      { name: "You", color: "rgba(66, 153, 225, 0.9)", value: 25000, probability: 12, isPlayer: true },
      { name: "Player 1", color: "rgba(56, 178, 172, 0.9)", value: 0, probability: 25 },
      { name: "Player 2", color: "rgba(49, 130, 206, 0.9)", value: 0, probability: 20 },
      { name: "Player 3", color: "rgba(44, 82, 130, 0.9)", value: 0, probability: 18 },
      { name: "Player 4", color: "rgba(26, 54, 93, 0.9)", value: 0, probability: 15 },
      { name: "Player 5", color: "rgba(0, 30, 60, 0.9)", value: 0, probability: 10 }
    ];
  }
  
  return wheelSegments;
};

/**
 * Adds lottery tickets when a user reveals a non-winning card
 * @param {string} userId - The user ID
 * @param {string} batchId - The batch ID
 * @param {string} cardId - The card ID
 * @param {number} ticketValue - The number of tickets to add (1-5)
 * @returns {Object} - The updated user lottery tickets
 */
export const addLotteryTicketsForCard = (userId, batchId, cardId, ticketValue) => {
  return LotteryDatabase.addLotteryTickets(userId, batchId, cardId, ticketValue);
};

/**
 * Performs the game draw for a batch
 * @param {string} batchId - The batch ID
 * @param {string} randomSeed - The random seed (can be from blockchain or generated)
 * @returns {Object} - The draw result
 */
export const performGameDraw = (batchId, randomSeed) => {
  // If no random seed provided, generate one
  const seed = randomSeed || Math.random().toString(36).substring(2, 15) + 
                            Math.random().toString(36).substring(2, 15);
  
  return LotteryDatabase.performGameDraw(batchId, seed);
};

/**
 * Gets a user's win probability for a specific batch
 * @param {string} userId - The user ID
 * @param {string} batchId - The batch ID
 * @returns {number} - The win probability (0-100)
 */
export const getUserWinProbability = (userId, batchId) => {
  return LotteryDatabase.getUserWinProbability(userId, batchId);
};

/**
 * Gets batch information for the Fortune Wheel's Batch Info tab
 * @param {string} batchId - The batch ID
 * @returns {Object} - Formatted batch information for display
 */
export const getBatchInfoForDisplay = (batchId) => {
  const batchLottery = LotteryDatabase.getBatchLottery(batchId);
  
  // Get all participants for ticket distribution
  const participants = LotteryDatabase.getAllParticipantTickets(batchId);
  
  // Sort participants by ticket count
  const sortedParticipants = [...participants].sort((a, b) => b.ticketCount - a.ticketCount);
  
  return {
    batchId: batchId,
    totalTickets: batchLottery.totalTickets,
    participantCount: batchLottery.participants.length,
    isDrawComplete: batchLottery.isDrawComplete,
    drawResult: batchLottery.drawResult,
    drawTimestamp: batchLottery.drawTimestamp,
    ticketDistribution: sortedParticipants.map((p, index) => ({
      name: `Player ${index + 1}`,
      value: p.ticketCount,
      percentage: (p.ticketCount / batchLottery.totalTickets) * 100
    })),
    // Format for prize distribution chart
    prizeDistribution: [
      { name: "Grand Prize", value: 1, amount: "25000" }
    ]
  };
};

/**
 * Gets user tickets and stats for display in the QuickCashGame UI
 * @param {string} userId - The user ID
 * @param {string} batchId - The batch ID
 * @returns {Object} - User ticket information formatted for the UI
 */
export const getUserStatsForDisplay = (userId, batchId) => {
  const userTickets = LotteryDatabase.getUserLotteryTickets(userId, batchId);
  const winProbability = LotteryDatabase.getUserWinProbability(userId, batchId);
  
  return {
    ticketCount: userTickets.ticketCount,
    winProbability: winProbability,
    recentTickets: userTickets.ticketDetails.slice(-5).reverse()
  };
};

/**
 * Determines if a batch is ready for a game draw
 * @param {string} batchId - The batch ID
 * @param {number} currentGamesPlayed - Current games played count
 * @param {number} batchSize - Total batch size
 * @returns {boolean} - Whether the batch is ready for a draw
 */
export const isBatchReadyForDraw = (batchId, currentGamesPlayed, batchSize) => {
  // Check if the batch is already complete in our database
  const batchLottery = LotteryDatabase.getBatchLottery(batchId);
  if (batchLottery.isDrawComplete) {
    return false; // Draw already completed
  }
  
  // Check if the batch is full based on games played
  return currentGamesPlayed >= batchSize;
};

/**
 * Gets the game state object for QuickCashGame with lottery ticket data
 * @param {Object} existingGameState - The existing game state object
 * @param {string} userId - The user ID
 * @returns {Object} - Updated game state with lottery ticket data
 */
export const getEnhancedGameState = (existingGameState, userId) => {
  if (!existingGameState || !userId) return existingGameState;
  
  const batchId = existingGameState.currentBatch?.toString() || "1";
  const userStats = getUserStatsForDisplay(userId, batchId);
  
  return {
    ...existingGameState,
    lotteryTickets: userStats.ticketCount,
    winProbability: userStats.winProbability,
    // Set auto-spin based on batch readiness
    demoAutoSpin: isBatchReadyForDraw(
      batchId, 
      existingGameState.gamesPlayed || 0, 
      existingGameState.batchSize || 100
    )
  };
};
